import React, { useContext, useState } from 'react';
import Select from '../common/Select';
import TagBar from '../common/TagBar';
import { TagProps } from '../common/Tag';
import { SectionColor } from '../common/Section';
import MultilineTooltip from '../common/MultilineTooltip';
import { TRANSLATIONS } from '../../constants/Translations';
import { useHook } from '../../hooks';
import { useAppDispatch, useAppSelector, useTranslate } from '../../hooks/common';
import { getDefaultFilter, getDefaultLoadData, getDefaultRatedPowerBasedOn, getDefaultStartingVoltage, getDefaultVoltage, getInertiaUnit, getTorqueUnit, isNullOrUndefined, mapToSliderInputText, nonProduction } from '../../services';
import { ABSOLUTE_UNITS, DEFAULT_AC, DEFAULT_AW, DEFAULT_EXPLOSION_PROTECTION, DEFAULT_FREQUENCY, DEFAULT_GEARBOX_INERTIA, DEFAULT_LANGUAGE_VALUE, DEFAULT_STARTING_CONDITION, DEFAULT_STARTING_TYPE, DEFAULT_STARTING_VOLTAGE, INITIAL_LOAD_TAG_VALUES } from '../../constants';
import { setGearboxInertiaError, setGearboxInputData, setLoadInertiaError, setLoadSliderTexts, setN2Error, setStartingVoltageText, setTags } from '../../store';
import { CommonData, Filter, LoadSliderInputRanges, Range, StartingConstraintsCondition, Tags } from '../../models';
import { LoadType, MotorDriveConnection, StartingCondition, StartingType, VsdOperationType } from '../../enums';
import { HomeContext } from './Home';
import { InnoButton, InnoIcon } from '@innomotics/innomotics-build-react-lib';
import { MLFB_REGEX } from '../../constants';

interface SelectorTagBarProps {
    backgroundColor: SectionColor;
    showVsdOperationSelect: boolean;
    disabled?: boolean;
    onReset: () => void;
    onCreateDocument: () => void;
    onVsdValueChange: (value: VsdOperationType) => void;
}

const SelectorTagBar = ({ disabled, onReset, showVsdOperationSelect, onCreateDocument, onVsdValueChange, backgroundColor }: SelectorTagBarProps) => {
    const dispatch = useAppDispatch();
    const translate = useTranslate();
    const filter = useAppSelector(state => state.motor.filter);
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const calculationResponse = useAppSelector(state => state.motor.calculationResponse);
    const loadSliderRanges = useAppSelector(state => state.load.loadSliderRanges);
    const vsdOperationType = useAppSelector(state => state.motor.vsdOperationType);
    const filterValues = useAppSelector(state => state.motor.filterValues);
    const tag = useAppSelector(state => state.motor.tags);
    const inertiaUnit = getInertiaUnit(mesRequest.unitSystem);
    const torqueUnit = getTorqueUnit(mesRequest.unitSystem);
    const getTagProps = (text: string | JSX.Element, onClick: () => void): TagProps => ({ text, onClick, close: true });
    const { getMainBoundary } = useContext(HomeContext);
    const { onFilterChange, onPreselectedReset } = useHook(x => x.filter)();
    const { onGearboxChange } = useHook(x => x.gearboxData)();
    const { onStartingConstraintChange } = useHook(x => x.startingConstraint)();
    const { onDiagramChange } = useHook(x => x.diagram)();
    const mlfbs = useAppSelector(state => state.motor.filterValues.mlfbs);
    const [searchText, setSearchText] = useState('');


    const {
        onN1SliderChange,
        onN2SliderInputChange,
        onN3SliderInputChange,
        onN4SliderInputChange,
        onM0SliderInputChange,
        onM1SliderChange,
        onM2SliderChange,
        onM3SliderChange,
        onM4SliderChange,
        calculateLoadTextAndRange,
        onLoadChange
    } = useHook(x => x.loadData)();

    const handleSearchChange = (value: string) => {
        if (MLFB_REGEX.test(value)) {
            setSearchText(value);
        }
    };

    const handleSearchSelect = (value: string) => {
        const newFilter: Partial<Filter> = { mlfb: value };

        setSearchText(value);
        onFilterChange(newFilter, newFilter);
    };

    const getTagText = (value: string | number | JSX.Element, translatedLabel: string, unit?: string | JSX.Element): string | JSX.Element => {
        const translatedLabelUnitIndex = translatedLabel.search(/\[.+\]/);
        const currentUnit = translatedLabelUnitIndex !== -1
            ? translatedLabel.substring(translatedLabelUnitIndex, translatedLabel.indexOf(']', translatedLabelUnitIndex) + 1)
            : unit;
        const translatedLabelText = translatedLabelUnitIndex !== -1 && typeof currentUnit === 'string'
            ? translatedLabel.replace(currentUnit, '').replace(/  +/g, ' ').trim()
            : translatedLabel;

        return currentUnit
            ? `${value} ${currentUnit} (${translatedLabelText})`
            : `${value} (${translatedLabelText})`;
    };

    const handleLoadTypeRest = async () => {
        const defaultLoadData = getDefaultLoadData(LoadType.QuadraticWithoutBreakaway, mesRequest);

        calculateLoadTextAndRange(defaultLoadData);
        dispatch(setTags({ loadType: undefined, ...INITIAL_LOAD_TAG_VALUES }));

        await onLoadChange(defaultLoadData);
    };


    const handleLoadInertiaReset = async () => {
        dispatch(setGearboxInputData({ loadInertia: DEFAULT_GEARBOX_INERTIA.toString() }));
        dispatch(setLoadInertiaError(true));

        await onGearboxChange({ loadInertia: DEFAULT_GEARBOX_INERTIA });

        dispatch(setTags({ loadInertia: undefined }));
    };

    const handleGearboxInertiaReset = async () => {
        dispatch(setGearboxInputData({ gearboxInertia: DEFAULT_GEARBOX_INERTIA.toString() }));
        dispatch(setGearboxInertiaError(true));

        await onGearboxChange({ gearboxInertia: DEFAULT_GEARBOX_INERTIA });

        dispatch(setTags({ gearboxInertia: undefined }));
    };

    const handleN2Reset = async () => {
        dispatch(setGearboxInputData({ n2: mesRequest.gearboxData.n1?.toString() ?? '' }));
        dispatch(setN2Error(true));

        await onGearboxChange({ n2: mesRequest.gearboxData.n1 });

        dispatch(setTags({ gearboxN2: undefined }));
    };

    const handleMotorDriveConnectionReset = async () => {
        dispatch(setN2Error(true));
        dispatch(setGearboxInertiaError(true));
        dispatch(setGearboxInputData({ n2: mesRequest.gearboxData.n1?.toString() ?? '', gearboxInertia: DEFAULT_GEARBOX_INERTIA.toString() }));

        await onGearboxChange({ n2: mesRequest.gearboxData.n1, gearboxInertia: DEFAULT_GEARBOX_INERTIA, motorDriveConnection: MotorDriveConnection.Direct });

        dispatch(setTags({ gearboxN2: undefined, gearboxInertia: undefined }));
    };

    const handleReset = async (filter: Partial<Filter>, shouldAutoFillFilter = false) => {
        await onFilterChange(filter, filter, shouldAutoFillFilter);
    };

    const handleStartingVoltageReset = async () => {
        dispatch(setTags({ startingVoltage: undefined }));
        dispatch(setStartingVoltageText(mapToSliderInputText(DEFAULT_STARTING_VOLTAGE)));

        await onStartingConstraintChange({ startingVoltage: DEFAULT_STARTING_VOLTAGE });
    };

    const handleExplosionProtectionReset = () => onPreselectedReset(DEFAULT_EXPLOSION_PROTECTION, filterValues.explosionProtections, { explosionProtection: DEFAULT_EXPLOSION_PROTECTION }, true);

    const handleVoltageReset = () => {
        const currentDefaultVoltage = getDefaultVoltage(filter.voltage);

        onPreselectedReset(currentDefaultVoltage, filterValues.voltages, { voltage: currentDefaultVoltage }, true);
    };

    const handleRatedPowerBasedOnReset = async () => {
        const currentDefaultRatedPowerBasedOn = getDefaultRatedPowerBasedOn(filter.frequency);

        onPreselectedReset(currentDefaultRatedPowerBasedOn, filterValues.ratedPowerBasedOns, { ratedPowerBasedOn: currentDefaultRatedPowerBasedOn }, true);
    };

    const handleScalingReset = async () => {
        dispatch(setTags({ scaling: undefined }));

        await onDiagramChange(ABSOLUTE_UNITS);
    };

    const handleNMTagClick = async (
        property: keyof LoadSliderInputRanges,
        handleOnSliderInputChange: (value: any) => void
    ) => {
        let defaultValue = getDefaultLoadData(mesRequest.loadData.loadType, mesRequest)[property] ?? 0;
        const { min, max } = loadSliderRanges[property] as Range;

        const lessThanMin = defaultValue < min;

        if (lessThanMin || defaultValue > max) {
            defaultValue = lessThanMin ? min : max;
        }

        dispatch(setLoadSliderTexts({ [property]: mapToSliderInputText(defaultValue) }));
        dispatch(setTags({ [property]: undefined }));

        handleOnSliderInputChange(defaultValue);
    };

    const handleStartupNumbersReset = async (property: StartingConstraintsCondition) => {
        const subset: Partial<CommonData> = { [property]: property === 'ac' ? DEFAULT_AC : DEFAULT_AW };

        handleStartingConstraintReset(property, subset);
    };

    const handleToleranceReset = () => {
        const subset: Partial<CommonData> = { isBreakdownToleranceOn: false, isLockedRotorPullupToleranceOn: false };

        handleStartingConstraintReset('isBreakdownToleranceOn', subset, true);
    };

    const handleStartingTypeReset = async () => {
        const startingVoltage = getDefaultStartingVoltage(mesRequest.loadData.loadType);

        dispatch(setTags({ startingVoltage: undefined, startingType: undefined }));
        dispatch(setStartingVoltageText(mapToSliderInputText(startingVoltage)));

        await onStartingConstraintChange({ startingVoltage: startingVoltage, startingType: DEFAULT_STARTING_TYPE });
    };

    const handleStartingConstraintReset = async (property: keyof Tags, subset: Partial<CommonData>, tolarenceChanged = false) => {
        dispatch(setTags({ [property]: undefined }));

        await onStartingConstraintChange(subset, tolarenceChanged);
    };

    const tags: TagProps[] = [
        ...tag && tag.frequency
            ? [getTagProps(getTagText(tag && tag.frequency, translate(TRANSLATIONS.filters.motorSelection.lineFrequency)), () => handleReset(getDefaultFilter(DEFAULT_FREQUENCY), true))]
            : [],
        ...tag && tag.ratedPowerBasedOn
            ? [getTagProps(getTagText(tag && tag.ratedPowerBasedOn, translate(TRANSLATIONS.filters.motorSelection.ratedPowerBasedOn)), handleRatedPowerBasedOnReset)]
            : [],
        ...tag && tag.power
            ? [getTagProps(getTagText(tag && tag.power, translate(TRANSLATIONS.filters.motorSelection.ratedPower)), () => handleReset({ power: null }))]
            : [],
        ...tag && tag.voltage
            ? [getTagProps(getTagText(tag && tag.voltage, translate(TRANSLATIONS.filters.motorSelection.lineVoltage)), handleVoltageReset)]
            : [],
        ...tag && tag.efficiencyClass
            ? [getTagProps(getTagText(tag && tag.efficiencyClass, translate(TRANSLATIONS.filters.motorSelection.efficiencyClass)), () => handleReset({ efficiencyClass: '' }))]
            : [],
        ...tag && tag.speed
            ? [getTagProps(getTagText(tag && tag.speed, translate(TRANSLATIONS.filters.motorSelection.speed)), () => handleReset({ speed: null }))]
            : [],
        ...tag && tag.explosionProtection && tag && tag.explosionProtection.en
            ? [getTagProps(getTagText(tag && tag.explosionProtection.en, translate(TRANSLATIONS.filters.motorSelection.explosionProtection.title)), handleExplosionProtectionReset)]
            : [],
        ...tag && tag.frameMaterial && tag && tag.frameMaterial.en
            ? [getTagProps(getTagText(tag && tag.frameMaterial.en, translate(TRANSLATIONS.filters.motorSelection.frameMaterial.title)), () => handleReset({ frameMaterial: DEFAULT_LANGUAGE_VALUE }))]
            : [],
        ...tag && tag.frameSize
            ? [getTagProps(getTagText(tag && tag.frameSize, translate(TRANSLATIONS.filters.motorSelection.frameSize)), () => handleReset({ frameSize: '' }))]
            : [],
        ...tag && tag.motorType && tag && tag.motorType.en
            ? [getTagProps(getTagText(tag && tag.motorType.en, translate(TRANSLATIONS.filters.motorSelection.motorType)), () => handleReset({ motorType: DEFAULT_LANGUAGE_VALUE }))]
            : [],
        ...tag && tag.mlfb
            ? [getTagProps(getTagText(tag && tag.mlfb, translate(TRANSLATIONS.filters.motorSelection.mlfb)), () => handleReset({ mlfb: '' }))]
            : [],
        ...tag && tag.loadInertia
            ? [getTagProps(getTagText(tag && tag.loadInertia, translate(TRANSLATIONS.filters.operationalData.loadCoupling.loadInertia), inertiaUnit), handleLoadInertiaReset)]
            : [],
        ...tag && tag.gearboxInertia
            ? [getTagProps(getTagText(tag && tag.gearboxInertia, translate(TRANSLATIONS.filters.operationalData.loadCoupling.gearboxInertia), inertiaUnit), handleGearboxInertiaReset)]
            : [],
        ...tag && tag.gearboxN2
            ? [getTagProps(getTagText(tag && tag.gearboxN2, translate(TRANSLATIONS.filters.operationalData.loadCoupling.n2)), handleN2Reset)]
            : [],
        ...tag && tag.motorDriveConnection != undefined
            ? [getTagProps(
                getTagText(translate((TRANSLATIONS.filters.operationalData.loadCoupling.motorDriveConnection as never)[MotorDriveConnection[tag.motorDriveConnection]]), translate(TRANSLATIONS.filters.operationalData.loadCoupling.motorDriveConnection.title)),
                handleMotorDriveConnectionReset
            )]
            : [],
        ...tag && tag.loadType != undefined
            ? [getTagProps(getTagText(translate((TRANSLATIONS.filters.operationalData.loadPoints.loadType as never)[LoadType[tag.loadType]]), translate(TRANSLATIONS.filters.operationalData.loadPoints.loadType.title)), handleLoadTypeRest)]
            : [],
        ...tag && tag.n1
            ? [getTagProps(getTagText(tag && tag.n1, translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n1)), () => handleNMTagClick('n1', onN1SliderChange))]
            : [],
        ...tag && tag.n2
            ? [getTagProps(getTagText(tag && tag.n2, translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n2)), () => handleNMTagClick('n2', onN2SliderInputChange))]
            : [],
        ...tag && tag.n3
            ? [getTagProps(getTagText(tag && tag.n3, translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n3)), () => handleNMTagClick('n3', onN3SliderInputChange))]
            : [],
        ...tag && tag.n4
            ? [getTagProps(getTagText(tag && tag.n4, translate(TRANSLATIONS.filters.operationalData.loadPointsSpeed.n4)), () => handleNMTagClick('n4', onN4SliderInputChange))]
            : [],
        ...tag && tag.m0
            ? [getTagProps(getTagText(tag && tag.m0, translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m0), torqueUnit), () => handleNMTagClick('m0', onM0SliderInputChange))]
            : [],
        ...tag && tag.m1
            ? [getTagProps(getTagText(tag && tag.m1, translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m1), torqueUnit), () => handleNMTagClick('m1', onM1SliderChange))]
            : [],
        ...tag && tag.m2
            ? [getTagProps(getTagText(tag && tag.m2, translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m2), torqueUnit), () => handleNMTagClick('m2', onM2SliderChange))]
            : [],
        ...tag && tag.m3
            ? [getTagProps(getTagText(tag && tag.m3, translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m3), torqueUnit), () => handleNMTagClick('m3', onM3SliderChange))]
            : [],
        ...tag && tag.m4
            ? [getTagProps(getTagText(tag && tag.m4, translate(TRANSLATIONS.filters.operationalData.loadPointsTorque.m4), torqueUnit), () => handleNMTagClick('m4', onM4SliderChange))]
            : [],
        ...tag && !isNullOrUndefined(tag.scaling)
            ? [getTagProps(getTagText(translate((TRANSLATIONS.diagram.scaling as never)[tag.scaling ? 'relatedScaling' : 'absoluteScaling']), translate(TRANSLATIONS.diagram.scaling.title)), handleScalingReset)]
            : [],
        ...tag && tag.startingVoltage
            ? [getTagProps(getTagText(`${tag && tag.startingVoltage}%`, translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingVoltage)), handleStartingVoltageReset)]
            : [],
        ...tag && tag.aw
            ? [getTagProps(getTagText(tag && tag.aw, translate(TRANSLATIONS.filters.operationalData.startingConstraints.aw)), () => handleStartupNumbersReset('aw'))]
            : [],
        ...tag && tag.ac
            ? [getTagProps(getTagText(tag && tag.ac, translate(TRANSLATIONS.filters.operationalData.startingConstraints.ac)), () => handleStartupNumbersReset('ac'))]
            : [],
        ...tag && !isNullOrUndefined(tag.isBreakdownToleranceOn)
            ? [getTagProps(getTagText(translate((TRANSLATIONS.filters.operationalData.startingConstraints.tolerance as never)[tag.isBreakdownToleranceOn ? 'withTolerance' : 'withoutTolerance']), translate(TRANSLATIONS.filters.operationalData.startingConstraints.tolerance.title)), handleToleranceReset)]
            : [],
        ...tag && tag.startingCondition != undefined
            ? [getTagProps(getTagText(translate((TRANSLATIONS.filters.operationalData.startingConstraints.startingCondition as never)[StartingCondition[tag.startingCondition]]), translate(TRANSLATIONS.filters.operationalData.startingConstraints.tolerance.title)), () => handleStartingConstraintReset('startingCondition', { startingCondition: DEFAULT_STARTING_CONDITION }))]
            : [],
        ...tag && tag.startingType != undefined
            ? [getTagProps(getTagText(translate((TRANSLATIONS.filters.operationalData.startingConstraints.startingType as never)[StartingType[tag.startingType]]), translate(TRANSLATIONS.filters.operationalData.startingConstraints.startingType.title)), handleStartingTypeReset)]
            : []
    ];
    
    const renderVsdOperationSelect = () => {
        if (calculationResponse) {
            const { isDolOperation, isVsdOperation } = calculationResponse.mesParameters.inputData.vsdMotorData;
            const selectedVsdOperationValues: { [key in VsdOperationType]?: JSX.Element }[] = [
                { '0': <>{translate((TRANSLATIONS.chipsLane.vsdCases as never)[VsdOperationType[0]])} <span className='vsd-mark'>VSD</span></> },
                { '1': <>{translate((TRANSLATIONS.chipsLane.vsdCases as never)[VsdOperationType[1]])}</> },
                { '2': <>{translate((TRANSLATIONS.chipsLane.vsdCases as never)[VsdOperationType[2]])} <span className='vsd-mark'>VSD</span></> }
            ];

            return <Select
                disabled={!isDolOperation && isVsdOperation}
                label=''
                mapToString={x => Object.values(x)[0] as JSX.Element}
                value={selectedVsdOperationValues[vsdOperationType]}
                values={selectedVsdOperationValues}
                onSelect={x => onVsdValueChange(Number(Object.keys(x)[0]))}
            />;
        }
    };

    const renderTagBarActions = () => (
        <>
            {nonProduction && showVsdOperationSelect && renderVsdOperationSelect()}
            <InnoButton color-variant='dark' variant='secondary' onClick={onReset}>
                {translate(TRANSLATIONS.chipsLane.reset)}
            </InnoButton>
            <InnoButton disabled={disabled} variant='primary' onClick={onCreateDocument}>
                {translate(TRANSLATIONS.chipsLane.createDocument)}
            </InnoButton>
            <MultilineTooltip idName='tagBarInfo' text={translate(TRANSLATIONS.tooltips.tagbar)} html>
                <></>
            </MultilineTooltip>
        </>
    );

    return (
        <div className='selector-tag-bar'>
            <TagBar tags={tags} backgroundColor={backgroundColor} actions={renderTagBarActions()} />
        </div>
    );
};

export default SelectorTagBar;
